import 'react-app-polyfill/ie11'; // For IE 11 support
import 'react-app-polyfill/stable';
import React, {Suspense, useEffect, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';
import {HelmetProvider} from 'react-helmet-async';
import {RouteSEO} from './components/SEO/RouteSEO';
import {GlobalStyle} from './styles/styles';
import './styles/fonts.css';
import {BrowserRouter, Route, Routes, Navigate, useParams, useLocation} from 'react-router-dom';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import routes from './routes/index';
import AuthRoute from './components/route/AuthRoute';
import {useUserStore} from 'store/user';
import GlobalAlertModal from 'components/modal/GlobalAlert';
import GlobalToast from 'components/common/GlobalToast';
import {useKeyboardDetect} from 'hooks/useKeyboardDetect';
import {useBottomModalStore} from 'store/bottomModal';
import BottomModal from 'components/modal/bottom';
import {createPortal} from 'react-dom';
import {useImagePickerStore} from 'store/imagePicker';
import {useChatStore} from 'store/chat';
import {initializePlatform} from 'utils/platform';
import {MessageType} from 'store/types';
import {useNotiStore} from 'store/noti';
import {updateNotiSetting} from 'services/notiSettingService';
import 'hooks/staleSentinelCleaner';

if (process.env.NODE_ENV === 'development') {
    require('hooks/history-debug');
}

// 화면 렌더링 전 플랫폼 메시지 리스너 우선 즉시 등록
if (window.ReactNativeWebView && !window.platformMessageListenerRegistered) {
    const handleEarlyPlatformMessage = (event: MessageEvent) => {
        try {
            if (typeof event.data === 'string' && event.data.includes('GET_PLATFORM_CALLBACK')) {
                const data = JSON.parse(event.data);
                if (data.type === 'GET_PLATFORM_CALLBACK') {
                    console.log('[IPC Recv] GET_PLATFORM_CALLBACK received (early):', data);
                    window.cachedPlatform = data.platform;
                    window.platformCachedPlatform = data.platform;
                }
            }
        } catch (error) {
            console.error('Early platform message parsing error:', error);
        }
    };

    window.addEventListener('message', handleEarlyPlatformMessage);
    document.addEventListener('message', handleEarlyPlatformMessage as EventListener);
    window.platformMessageListenerRegistered = true;
    console.log('Early platform message listener registered');
}

// 프로덕션 환경에서 console.log 비활성화 처리
if (process.env.NODE_ENV === 'production') {
    console.log = () => {};
    console.warn = () => {};
    console.info = () => {};
}

const loading = (
    <div
        style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#222',
            fontSize: '14px',
            fontWeight: '400',
            lineHeight: '20px',
            letterSpacing: '-0.28px',
        }}
    >
        화면을 불러오는 중 입니다.
    </div>
);

const queryClient = new QueryClient();

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));

function App() {
    useKeyboardDetect();
    const {isLoggedIn, initialize, managePushToken} = useUserStore();
    const [isInitialized, setIsInitialized] = useState(false);
    const {setNotificationYn, setLoading} = useNotiStore();
    const {
        isOpen: isBottomModalOpen,
        content: bottomModalContent,
        close: closeBottomModal,
        onConfirm: bottomModalOnConfirm,
        title: bottomModalTitle,
        heightAuto: bottomModalHeightAuto,
        radius: bottomModalRadius,
    } = useBottomModalStore();

    const fileInputRef = useRef<HTMLInputElement>(null);
    const {registerInputCallbacks, handleFileChange, initializeImagePicker} = useImagePickerStore();

    // 기타 메시지 핸들러 등록
    useEffect(() => {
        const handleOtherMessages = (event: MessageEvent) => {
            try {
                // WEB_SEND_TOKEN 메시지는 별도 처리
                if (typeof event.data === 'string' && event.data.includes('WEB_SEND_TOKEN')) {
                    return;
                }

                // GET_PLATFORM_CALLBACK은 이미 early listener에서 처리됨
                if (typeof event.data === 'string' && event.data.includes('GET_PLATFORM_CALLBACK')) {
                    return;
                }

                // 앱 상태 변화 처리 (SSE 재연결 트리거)
                if (typeof event.data === 'string' && event.data.includes('APP_STATE_CHANGE')) {
                    const data = JSON.parse(event.data);
                    if (data.type === 'APP_STATE_CHANGE' && data.state === 'active') {
                        console.log('[App State] 앱 포그라운드 복귀 감지');
                        document.dispatchEvent(new CustomEvent('app-state-active'));
                    }
                    return;
                }

                // 포그라운드 push 수신 처리 (WKWebView JS 깨우기)
                if (typeof event.data === 'string' && event.data.includes('CHAT_PUSH_RECEIVED')) {
                    const data = JSON.parse(event.data);
                    if (data.type === 'CHAT_PUSH_RECEIVED') {
                        console.log('[IPC Recv] CHAT_PUSH_RECEIVED');
                        document.dispatchEvent(new CustomEvent('chat-push-received'));
                    }
                    return;
                }

                // 마이페이지 > 알림 권한 결과 처리
                if (typeof event.data === 'string' && event.data.includes('UPDATE_NOTIFICATION_PERMISSION_RESULT')) {
                    const data = JSON.parse(event.data);
                    if (data.type === MessageType.UPDATE_NOTIFICATION_PERMISSION_RESULT) {
                        const permissionStatus = data.data; // "enabled" | "disabled"

                        if (permissionStatus !== 'enabled' && permissionStatus !== 'disabled') {
                            console.warn('예상하지 못한 알림 권한 상태:', permissionStatus);
                            return;
                        }

                        const newNotificationYn = permissionStatus === 'enabled';

                        const handleNotificationUpdate = async () => {
                            try {
                                setLoading(true);
                                const result = await updateNotiSetting(newNotificationYn);
                                console.log('알림 설정 업데이트 성공:', result);

                                setNotificationYn(result);
                            } catch (error) {
                                console.error('알림 설정 업데이트 실패:', error);
                            } finally {
                                setLoading(false);
                            }
                        };

                        handleNotificationUpdate();
                    }
                }
            } catch (error) {
                console.log(' 기타 메시지 파싱 오류', error);
            }
        };

        if (window.ReactNativeWebView) {
            window.addEventListener('message', handleOtherMessages, true);
        }

        return () => {
            if (window.ReactNativeWebView) {
                window.removeEventListener('message', handleOtherMessages, true);
            }
        };
    }, [setLoading, setNotificationYn]);

    useEffect(() => {
        registerInputCallbacks({
            triggerGallery: () => {
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                    fileInputRef.current.removeAttribute('capture');
                    fileInputRef.current.setAttribute('accept', 'image/*');

                    // 파일 선택 취소 감지를 위한 플래그
                    let fileSelected = false;

                    // change 이벤트 리스너 (파일 선택됨)
                    const handleChange = () => {
                        fileSelected = true;
                        window.removeEventListener('focus', handleFocus);
                    };

                    // focus 이벤트 리스너 (선택기 닫힘)
                    const handleFocus = () => {
                        // 약간의 딜레이 후 체크 (change 이벤트가 먼저 발생할 수 있음)
                        setTimeout(() => {
                            if (!fileSelected) {
                                // 취소됨 - 상태 리셋
                                useImagePickerStore.getState().setIsUploadingImage(false);
                                useChatStore.getState().setIsImageUploadMode(false);
                                useChatStore.getState().setImageUploadMessageId(null);
                            }
                            window.removeEventListener('focus', handleFocus);
                        }, 100);
                    };

                    fileInputRef.current.addEventListener('change', handleChange, {once: true});
                    window.addEventListener('focus', handleFocus, {once: true});

                    fileInputRef.current.click();
                }
            },
            triggerCamera: () => {
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                    fileInputRef.current.setAttribute('capture', 'environment');
                    fileInputRef.current.click();
                }
            },
            clearInput: () => {
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            },
        });
    }, [registerInputCallbacks]);

    // 이미지 피커 초기화
    useEffect(() => {
        if (window.ReactNativeWebView) {
            initializeImagePicker();
        }
    }, [initializeImagePicker]);

    useEffect(() => {
        const initAppAuth = async () => {
            await initialize();
            await initializePlatform();
            setIsInitialized(true);

            // 웹뷰 초기화 완료 후 준비완료 신호 전송
            if (window.ReactNativeWebView) {
                const message = {
                    type: 'WEBVIEW_READY',
                };
                window.ReactNativeWebView.postMessage(JSON.stringify(message));
                console.log('웹뷰 준비 완료 신호 전송');
            }
        };

        initAppAuth();
    }, [initialize]);

    useEffect(() => {
        if (isLoggedIn) {
            managePushToken();
        }
    }, [isLoggedIn, managePushToken]);

    // 키보드 변경시 Viewport 값이 바뀌는 기기: 키보드가 올라왔을 때 하단 safe-area padding 제거
    useEffect(() => {
        const root = document.getElementById('root');
        if (!root || !window.visualViewport) return;

        const baseHeight = window.visualViewport.height;

        const handler = () => {
            const isKeyboardOpen = (window.visualViewport?.height ?? 0) < baseHeight - 100; // 100px 이상 줄면 키보드 열림
            if (isKeyboardOpen) {
                root.style.paddingBottom = '0px'; // safe area padding 제거
            } else {
                root.style.paddingBottom = 'var(--safe-bottom-keyboard)';
            }
        };

        window.visualViewport.addEventListener('resize', handler);
        return () => {
            window.visualViewport?.removeEventListener('resize', handler);
        };
    }, []);

    if (!isLoggedIn && !isInitialized) {
        return loading;
    }

    return (
        <HelmetProvider>
            <QueryClientProvider client={queryClient}>
                <GlobalStyle />
                <input
                    type='file'
                    ref={fileInputRef}
                    onChange={e => handleFileChange(e)}
                    accept='image/*'
                    multiple
                    style={{display: 'none'}}
                />
                <BrowserRouter>
                    <Suspense fallback={loading}>
                        <Routes>
                            {routes.map(route => {
                                const PageContent = (
                                    <RouteLayoutWrapper route={route}>
                                        <route.element />
                                    </RouteLayoutWrapper>
                                );

                                if (route.auth) {
                                    return (
                                        <Route
                                            key={route.path}
                                            path={route.path}
                                            element={<AuthRoute>{PageContent}</AuthRoute>}
                                        />
                                    );
                                } else {
                                    return <Route key={route.path} path={route.path} element={PageContent} />;
                                }
                            })}
                            <Route path='/*' element={<Navigate to='/404' />} />
                        </Routes>
                    </Suspense>
                </BrowserRouter>
                {createPortal(
                    <BottomModal
                        isOpen={isBottomModalOpen}
                        onClose={closeBottomModal}
                        onConfirm={bottomModalOnConfirm || (() => {})}
                        title={bottomModalTitle}
                        heightAuto={bottomModalHeightAuto}
                        radius={bottomModalRadius}
                    >
                        {bottomModalContent}
                    </BottomModal>,
                    document.body,
                )}
                {createPortal(<GlobalToast />, document.body)}
                {createPortal(<GlobalAlertModal />, document.body)}
                <ReactQueryDevtools initialIsOpen={true} />
            </QueryClientProvider>
        </HelmetProvider>
    );
}

function RouteLayoutWrapper({route, children}: {route: any; children: React.ReactNode}) {
    const params = useParams();
    const location = useLocation();

    const layoutConfig = route.getLayout ? route.getLayout(params, location) : {};

    React.useLayoutEffect(() => {
        const resetScroll = () => {
            const contentElement = document.querySelector('.content-wrapper') as HTMLElement;
            if (contentElement) {
                contentElement.scrollTop = 0;
            }
        };

        resetScroll();
    }, [location.pathname]);

    return (
        <DefaultLayout layout={layoutConfig}>
            <RouteSEO />
            {children}
        </DefaultLayout>
    );
}

const container = document.getElementById('root');
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);
root.render(<App />);
